import { useCallback, useEffect, useState, useRef } from 'react';
import { message, Modal, notification } from 'antd';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import PropostaService from '../../../services/http/services/Propostas/PropostaService';
import ConfiguracaoGeralPropostaService from '../../../services/http/services/Propostas/ConfiguracaoGeralPropostaService';
import { IProposta, IPropostaListItem, IPropostaFilter, ISlidePropostaData, IConfiguracaoGeralProposta } from '../interface';
import { getUsuario } from '../../../services/token/usuario';
import { EmitirErro } from '../../../services/EmitirErro';

export const usePropostas = () => {
    const history = useHistory();
    const cancelTokenSource = useRef(axios.CancelToken.source());

    const [propostas, setPropostas] = useState<IPropostaListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<IPropostaFilter>({});
    const [search, setSearch] = useState<string>('');
    const [modalEnvioVisible, setModalEnvioVisible] = useState<boolean>(false);
    const [propostaSelecionada, setPropostaSelecionada] = useState<IPropostaListItem | IProposta | null>(null);
    const [statusEnvioDesejado, setStatusEnvioDesejado] = useState<boolean>(true);
    const [enviadoPara, setEnviadoPara] = useState<string>('');
    const [novoEnvioPara, setNovoEnvioPara] = useState<string>('');
    const [novoEnvioObs, setNovoEnvioObs] = useState<string>('');
    const [loadingEnvio, setLoadingEnvio] = useState<boolean>(false);
    const [loadingRegistrarEnvio, setLoadingRegistrarEnvio] = useState<boolean>(false);
    const [previewVisible, setPreviewVisible] = useState<boolean>(false);
    const [propostaVisualizar, setPropostaVisualizar] = useState<IProposta | null>(null);
    const [configGeral, setConfigGeral] = useState<IConfiguracaoGeralProposta | null>(null);

    useEffect(() => {
        ConfiguracaoGeralPropostaService.get().then(res => {
            if (res) setConfigGeral(res);
        }).catch(() => null);
    }, []);

    const carregarPropostas = useCallback(async (filtrosAplicados?: IPropostaFilter) => {
        setLoading(true);
        try {
            const currentFilters = filtrosAplicados ?? filters;
            
            let enviadoParam: boolean | undefined = undefined;
            if (currentFilters.status === 'enviada') enviadoParam = true;
            else if (currentFilters.status === 'nao_enviada') enviadoParam = false;
            else if (currentFilters.enviado !== undefined) enviadoParam = currentFilters.enviado;

            const paramsToSend: Record<string, any> = {
                ...currentFilters,
                status: currentFilters.status === 'todos' ? undefined : currentFilters.status,
                enviado: enviadoParam
            };

            Object.keys(paramsToSend).forEach(key => {
                if (paramsToSend[key] === undefined) {
                    delete paramsToSend[key];
                }
            });

            const response = await PropostaService.getAllPropostas(
                paramsToSend,
                cancelTokenSource.current.token
            );
            const lista = Array.isArray(response) ? response : (Array.isArray((response as any)?.data) ? (response as any).data : []);
            setPropostas(lista);
        } catch (error: any) {
            if (!axios.isCancel(error)) {
                EmitirErro('Erro ao carregar a listagem de propostas.');
                console.error(error);
            }
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        carregarPropostas();

        return () => {
            cancelTokenSource.current.cancel();
        };
    }, []);

    const handleSearch = (valor: string) => {
        setSearch(valor);
        const novosFiltros: IPropostaFilter = { ...filters, search: valor ? valor.trim() : undefined };
        setFilters(novosFiltros);
        carregarPropostas(novosFiltros);
    };

    const handleFilterStatus = (status?: 'enviada' | 'nao_enviada' | 'todos' | string) => {
        const statusReal = !status || status === 'todos' ? undefined : (status as 'enviada' | 'nao_enviada');
        let enviadoParam: boolean | undefined = undefined;
        if (statusReal === 'enviada') enviadoParam = true;
        else if (statusReal === 'nao_enviada') enviadoParam = false;

        const novosFiltros: IPropostaFilter = { 
            ...filters, 
            status: statusReal,
            enviado: enviadoParam 
        };
        setFilters(novosFiltros);
        carregarPropostas(novosFiltros);
    };

    const handleDuplicar = async (id: number) => {
        Modal.confirm({
            title: 'Duplicar Proposta',
            content: 'Deseja realmente gerar uma cópia desta proposta?',
            okText: 'Sim, duplicar',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await PropostaService.duplicateProposta(id);
                    message.success('Proposta duplicada com sucesso!');
                    carregarPropostas();
                } catch (error: any) {
                    EmitirErro('Erro ao duplicar proposta.');
                    console.error(error);
                }
            }
        });
    };

    const handleOpenModalEnvio = (proposta: IPropostaListItem | IProposta) => {
        if (proposta.gerada === false) {
            message.warning('Esta proposta é uma cópia duplicada. Por favor, edite e salve a proposta antes de registrar o envio.');
            return;
        }
        setPropostaSelecionada(proposta);
        const isEnviada = Boolean(proposta.enviado || proposta.status === 'enviada');
        setStatusEnvioDesejado(isEnviada);
        setEnviadoPara(proposta.enviado_para || '');
        setNovoEnvioPara('');
        setNovoEnvioObs('');
        setModalEnvioVisible(true);
    };

    const handleRegistrarNovoEnvio = async () => {
        if (!propostaSelecionada) return;
        if (!novoEnvioPara || !novoEnvioPara.trim()) {
            message.warning('Por favor, informe o destinatário do envio (nome, órgão ou e-mail).');
            return;
        }

        setLoadingRegistrarEnvio(true);
        try {
            const propostaAtualizada = await PropostaService.registrarEnvio(propostaSelecionada.id, {
                enviado_para: novoEnvioPara.trim(),
                observacao: novoEnvioObs.trim() || undefined
            });

            notification.success({
                message: "Envio registrado!",
                description: `Envio para "${novoEnvioPara.trim()}" registrado com sucesso no histórico da proposta.`
            });

            setPropostaSelecionada(propostaAtualizada);
            setNovoEnvioPara('');
            setNovoEnvioObs('');
            carregarPropostas();
        } catch (error: any) {
            EmitirErro('Erro ao registrar novo envio.');
            console.error(error);
        } finally {
            setLoadingRegistrarEnvio(false);
        }
    };

    const handleExcluirEnvio = async (envioId: number) => {
        if (!propostaSelecionada) return;

        Modal.confirm({
            title: 'Excluir Registro de Envio',
            content: 'Deseja realmente remover este registro de envio do histórico?',
            okText: 'Sim, excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const propostaAtualizada = await PropostaService.excluirEnvio(propostaSelecionada.id, envioId);
                    message.success('Registro de envio excluído com sucesso!');
                    setPropostaSelecionada(propostaAtualizada);
                    carregarPropostas();
                } catch (error: any) {
                    EmitirErro('Erro ao excluir envio.');
                    console.error(error);
                }
            }
        });
    };

    const handleConfirmarStatusEnvio = async () => {
        if (!propostaSelecionada) return;

        setLoadingEnvio(true);
        try {
            await PropostaService.toggleStatusEnvio(
                propostaSelecionada.id, 
                statusEnvioDesejado, 
                statusEnvioDesejado ? enviadoPara : undefined
            );
            notification.success({
                message: "Sucesso !",
                description: `Status da proposta alterado para ${statusEnvioDesejado ? 'Enviada' : 'Não Enviada'}!`
            });
            setModalEnvioVisible(false);
            setPropostaSelecionada(null);
            carregarPropostas();
        } catch (error: any) {
            EmitirErro('Erro ao atualizar status de envio.');
            console.error(error);
        } finally {
            setLoadingEnvio(false);
        }
    };

    const handleVisualizar = async (proposta: IPropostaListItem | IProposta) => {
        try {
            setLoading(true);
            const fullProposta = await PropostaService.getPropostaById(proposta.id);
            if (fullProposta) {
                setPropostaVisualizar(fullProposta);
                setPreviewVisible(true);
            }
        } catch (error) {
            EmitirErro('Erro ao carregar dados da proposta para visualização.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleClosePreview = () => {
        setPreviewVisible(false);
        setPropostaVisualizar(null);
    };

    const getPropostaSlideData = (p: IProposta | null): ISlidePropostaData | null => {
        if (!p) return null;
        const loggedUser = getUsuario();

        const getCargoDesc = (cargo: any): string => {
            if (!cargo) return '';
            if (typeof cargo === 'object' && cargo.nome) return String(cargo.nome);
            if (typeof cargo === 'string') return cargo;
            return '';
        };

        const isGerada = p.gerada !== false;
        const criadorNome = isGerada ? (p.gerado_por?.nome || p.gerado_por_nome || '') : '';
        const vendedorNome = p.vendedor?.nome || p.vendedor_nome || criadorNome || loggedUser?.nome || '';
        const vendedorCargo = getCargoDesc(p.vendedor?.cargo)
            || (isGerada ? getCargoDesc(p.gerado_por?.cargo) : '')
            || getCargoDesc(loggedUser?.cargo)
            || '';
        const vendedorTelefone = p.vendedor?.celular || (isGerada ? p.gerado_por?.celular : '') || loggedUser?.celular || '';
        const vendedorEmail = p.vendedor?.email || (isGerada ? p.gerado_por?.email : '') || loggedUser?.email || '';

        const principalNome = p.contato_principal?.nome || p.contato_principal_nome || '';
        const principalCargo = getCargoDesc(p.contato_principal?.cargo) || '';
        const principalTelefone = p.contato_principal?.celular || '';
        const principalEmail = p.contato_principal?.email || '';

        return {
            id: p.id,
            uuid: p.uuid,
            titulo_cabecalho: p.titulo_cabecalho,
            cidade_nome: p.cidade_nome,
            cidade_uf: p.cidade_uf,
            cidade_completo: p.cidade_completo || (p.cidade_uf ? `${p.cidade_nome} - ${p.cidade_uf}` : p.cidade_nome),
            plano_nome: p.plano_nome || 'Licença Intermediária',
            descricao_plano: p.descricao_plano,
            texto_objeto: p.texto_objeto,
            quantidade_licencas: p.quantidade_licencas,
            valor_unitario_plano: p.valor_unitario_plano,
            valor_total_plano: p.valor_total_plano,
            contratacao_horas: p.contratacao_horas,
            quantidade_horas: p.quantidade_horas,
            valor_unitario_horas: p.valor_unitario_horas,
            valor_total_horas: p.valor_total_horas,
            valor_total_geral: p.valor_total_geral,
            condicao_pagamento: p.condicao_pagamento,
            forma_pagamento_descricao: p.forma_pagamento_descricao,
            detalhamento: p.detalhamento,
            possui_tela: p.possui_tela ?? true,
            tipo_tela: p.tipo_tela || (p.possui_tela ? 'tela' : null),
            gerado_em: isGerada ? (p.gerado_em || p.createdAt) : null,
            gerado_por_nome: isGerada ? (p.gerado_por_nome || p.gerado_por?.nome || null) : null,
            vendedor_nome: vendedorNome,
            vendedor_cargo: vendedorCargo,
            vendedor_telefone: vendedorTelefone,
            vendedor_email: vendedorEmail,
            contato_principal_nome: principalNome,
            contato_principal_cargo: principalCargo,
            contato_principal_telefone: principalTelefone,
            contato_principal_email: principalEmail,
            municipios_destaque: configGeral?.municipios_destaque,
            gerada: p.gerada !== false,
        };
    };

    const handleNovaProposta = () => {
        history.push('/propostas-comerciais/cadastrar');
    };

    const handleEditarProposta = (id: number) => {
        const prop = propostas.find(p => p.id === id);
        if (prop && prop.gerada === true) {
            message.warning("Esta proposta já foi gerada e não pode ser editada. Para fazer alterações, duplique a proposta.");
            return;
        }
        history.push(`/propostas-comerciais/editar/${id}`);
    };

    const handleExcluir = async (id: number) => {
        const prop = propostas.find(p => p.id === id);
        const isEnviada = Boolean(prop && (prop.enviado || prop.status === 'enviada'));
        if (isEnviada) {
            message.warning("Não é possível excluir uma proposta que já foi enviada.");
            return;
        }

        Modal.confirm({
            title: 'Excluir Proposta',
            content: 'Deseja realmente excluir esta proposta? Esta ação não pode ser desfeita.',
            okText: 'Sim, excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await PropostaService.deleteProposta(id);
                    message.success('Proposta excluída com sucesso!');
                    carregarPropostas();
                } catch (error: any) {
                    EmitirErro('Erro ao excluir proposta.');
                    console.error(error);
                }
            }
        });
    };

    return {
        propostas,
        loading,
        search,
        filters,
        modalEnvioVisible,
        propostaSelecionada,
        statusEnvioDesejado,
        enviadoPara,
        novoEnvioPara,
        novoEnvioObs,
        loadingEnvio,
        loadingRegistrarEnvio,
        previewVisible,
        propostaVisualizar,
        getPropostaSlideData,
        handleVisualizar,
        handleClosePreview,
        setStatusEnvioDesejado,
        setEnviadoPara,
        setNovoEnvioPara,
        setNovoEnvioObs,
        setModalEnvioVisible,
        handleSearch,
        handleFilterStatus,
        handleDuplicar,
        handleExcluir,
        handleOpenModalEnvio,
        handleConfirmarStatusEnvio,
        handleRegistrarNovoEnvio,
        handleExcluirEnvio,
        handleNovaProposta,
        handleEditarProposta,
        carregarPropostas
    };
};
